import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  StorageReference 
} from 'firebase/storage';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  DocumentReference 
} from 'firebase/firestore';
import { storage, db, isFirebaseInitialized } from '../../config/firebase';
import { 
  CanvasMetadata, 
  CreateCanvasMetadata, 
  UpdateCanvasMetadata,
  CloudSaveResult,
  CloudLoadResult,
  CloudSaveListResult
} from './canvasMetadata';

export class CloudSaveService {
  private static readonly CANVAS_COLLECTION = 'canvases';
  private static readonly STORAGE_PATH_PREFIX = 'canvases';

  /**
   * Save a canvas to the cloud
   */
  static async saveCanvas(
    content: string, 
    canvasName: string, 
    userId: string, 
    canvasId?: string,
    description?: string,
    tags?: string[]
  ): Promise<CloudSaveResult> {
    try {
      // Ensure Firebase is initialized
      if (!isFirebaseInitialized()) {
        throw new Error('Firebase is not initialized yet');
      }

      // Detect if content is JSON or YAML
      const isJson = content.trim().startsWith('{') || content.trim().startsWith('[');
      const fileExtension = isJson ? 'json' : 'yaml';
      const mimeType = isJson ? 'application/json' : 'text/yaml';

      // Convert content to Blob with appropriate MIME type
      const contentBlob = new Blob([content], { type: mimeType });
      const fileSize = contentBlob.size;

      // Determine storage path with correct extension
      const storagePath = canvasId 
        ? `${this.STORAGE_PATH_PREFIX}/${userId}/${canvasId}.${fileExtension}`
        : `${this.STORAGE_PATH_PREFIX}/${userId}/${Date.now()}.${fileExtension}`;

      // Check that storage is available
      if (!storage) {
        throw new Error('Firebase Storage is not available');
      }

      // Upload file to Firebase Storage
      const storageRef = ref(storage, storagePath);
      const uploadResult = await uploadBytes(storageRef, contentBlob);
      
      if (!uploadResult) {
        throw new Error('Failed to upload file to storage');
      }

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      if (canvasId) {
        // Update existing canvas
        return await this.updateCanvasMetadata(canvasId, {
          name: canvasName,
          lastModified: Timestamp.now(),
          fileSize,
          storagePath,
          description,
          tags
        });
      } else {
        // Create new canvas
        const metadata: CreateCanvasMetadata = {
          name: canvasName,
          ownerId: userId,
          storagePath,
          fileSize,
          description,
          tags
        };

        return await this.createCanvasMetadata(metadata);
      }
    } catch (error) {
      console.error('Error saving canvas to cloud:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Load a canvas from the cloud
   */
  static async loadCanvas(canvasId: string, userId: string): Promise<CloudLoadResult> {
    try {
      // Ensure Firebase is initialized
      if (!isFirebaseInitialized()) {
        throw new Error('Firebase is not initialized yet');
      }

      // Get canvas metadata
      const metadata = await this.getCanvasMetadata(canvasId);
      
      if (!metadata.success || !metadata.metadata) {
        return {
          success: false,
          error: metadata.error || 'Canvas not found'
        };
      }

      // Verify ownership
      if (metadata.metadata.ownerId !== userId) {
        return {
          success: false,
          error: 'Access denied: You do not own this canvas'
        };
      }

      // Check that storage is available
      if (!storage) {
        throw new Error('Firebase Storage is not available');
      }

      // Download YAML content from storage
      const storageRef = ref(storage, metadata.metadata.storagePath);
      const downloadURL = await getDownloadURL(storageRef);
      const response = await fetch(downloadURL);
      
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const content = await response.text();

      return {
        success: true,
        content,
        metadata: metadata.metadata
      };
    } catch (error) {
      console.error('Error loading canvas from cloud:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * List all canvases for a user
   */
  static async listUserCanvases(userId: string): Promise<CloudSaveListResult> {
    try {
      if (!db) {
        throw new Error('Firestore is not available');
      }

      const canvasesQuery = query(
        collection(db, this.CANVAS_COLLECTION),
        where('ownerId', '==', userId),
        orderBy('lastModified', 'desc')
      );

      const querySnapshot = await getDocs(canvasesQuery);
      const canvases: CanvasMetadata[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        canvases.push({
          id: doc.id,
          name: data.name,
          ownerId: data.ownerId,
          createdAt: data.createdAt,
          lastModified: data.lastModified,
          storagePath: data.storagePath,
          fileSize: data.fileSize,
          thumbnail: data.thumbnail,
          isPublic: data.isPublic,
          description: data.description,
          tags: data.tags
        });
      });

      return {
        success: true,
        canvases
      };
    } catch (error) {
      console.error('Error listing user canvases:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Delete a canvas from the cloud
   */
  static async deleteCanvas(canvasId: string, userId: string): Promise<CloudSaveResult> {
    try {
      // Get canvas metadata first
      const metadata = await this.getCanvasMetadata(canvasId);
      
      if (!metadata.success || !metadata.metadata) {
        return {
          success: false,
          error: metadata.error || 'Canvas not found'
        };
      }

      // Verify ownership
      if (metadata.metadata.ownerId !== userId) {
        return {
          success: false,
          error: 'Access denied: You do not own this canvas'
        };
      }

      // Check that storage is available
      if (!storage) {
        throw new Error('Firebase Storage is not available');
      }

      // Delete file from storage
      const storageRef = ref(storage, metadata.metadata.storagePath);
      await deleteObject(storageRef);

      // Check that db is available
      if (!db) {
        throw new Error('Firestore is not available');
      }

      // Delete metadata from Firestore
      const docRef = doc(db, this.CANVAS_COLLECTION, canvasId);
      await deleteDoc(docRef);

      // Delete canvas quota document to decrement usage counters
      try {
        const canvasQuotaRef = doc(db, 'users', userId, 'canvasQuotas', canvasId);
        await deleteDoc(canvasQuotaRef);
      } catch (quotaError) {
        // Don't fail the entire deletion if quota deletion fails
        console.warn(`⚠️ [CloudSaveService] Failed to delete canvas quota for canvas ${canvasId}:`, quotaError);
      }

      // Decrement user-level canvas counter
      try {
        const { decrementUsage } = await import('../../utils/firestoreApi');
        await decrementUsage(userId, 'totalCanvases');
      } catch (decrementError) {
        // Don't fail the entire deletion if decrement fails
        console.warn(`⚠️ [CloudSaveService] Failed to decrement canvasesCreated for user ${userId}:`, decrementError);
      }

      return {
        success: true,
        canvasId
      };
    } catch (error) {
      console.error('Error deleting canvas:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Update canvas metadata
   */
  static async updateCanvasMetadata(
    canvasId: string, 
    updates: UpdateCanvasMetadata
  ): Promise<CloudSaveResult> {
    try {
      if (!db) {
        throw new Error('Firestore is not available');
      }

      const docRef = doc(db, this.CANVAS_COLLECTION, canvasId);
      
      // Convert updates to plain object for Firestore
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.lastModified !== undefined) updateData.lastModified = updates.lastModified;
      if (updates.fileSize !== undefined) updateData.fileSize = updates.fileSize;
      if (updates.storagePath !== undefined) updateData.storagePath = updates.storagePath;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.tags !== undefined) updateData.tags = updates.tags;
      
      // Always update lastModified if not provided
      if (!updateData.lastModified) {
        updateData.lastModified = Timestamp.now();
      }
      
      await updateDoc(docRef, updateData);

      // Get updated metadata
      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Canvas not found after update');
      }

      const data = updatedDoc.data();
      const metadata: CanvasMetadata = {
        id: updatedDoc.id,
        name: data.name,
        ownerId: data.ownerId,
        createdAt: data.createdAt,
        lastModified: data.lastModified,
        storagePath: data.storagePath,
        fileSize: data.fileSize,
        thumbnail: data.thumbnail,
        isPublic: data.isPublic,
        description: data.description,
        tags: data.tags
      };

      return {
        success: true,
        canvasId,
        metadata
      };
    } catch (error) {
      console.error('Error updating canvas metadata:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get canvas metadata by ID
   */
  static async getCanvasMetadata(canvasId: string): Promise<CloudLoadResult> {
    try {
      if (!db) {
        throw new Error('Firestore is not available');
      }

      const docRef = doc(db, this.CANVAS_COLLECTION, canvasId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return {
          success: false,
          error: 'Canvas not found'
        };
      }

      const data = docSnap.data();
      const metadata: CanvasMetadata = {
        id: docSnap.id,
        name: data.name,
        ownerId: data.ownerId,
        createdAt: data.createdAt,
        lastModified: data.lastModified,
        storagePath: data.storagePath,
        fileSize: data.fileSize,
        thumbnail: data.thumbnail,
        isPublic: data.isPublic,
        description: data.description,
        tags: data.tags
      };

      return {
        success: true,
        metadata
      };
    } catch (error) {
      console.error('Error getting canvas metadata:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Create new canvas metadata
   */
  private static async createCanvasMetadata(
    metadata: CreateCanvasMetadata
  ): Promise<CloudSaveResult> {
    try {
      if (!db) {
        throw new Error('Firestore is not available');
      }

      const docData: { [key: string]: any } = {
        name: metadata.name,
        ownerId: metadata.ownerId,
        storagePath: metadata.storagePath,
        fileSize: metadata.fileSize,
        createdAt: Timestamp.now(),
        lastModified: Timestamp.now()
      };

      if (metadata.description) {
        docData.description = metadata.description;
      }

      if (metadata.tags && metadata.tags.length > 0) {
        docData.tags = metadata.tags;
      }

      const docRef = await addDoc(collection(db, this.CANVAS_COLLECTION), docData);
      
      // Increment canvas creation quota for new canvases
      try {
        const { incrementUsage } = await import('../../utils/firestoreApi');
        await incrementUsage(metadata.ownerId, 'totalCanvases');
      } catch (quotaError) {
        // Don't fail the entire creation if quota increment fails
        console.warn(`⚠️ [CloudSaveService] Failed to increment canvasesCreated for user ${metadata.ownerId}:`, quotaError);
      }
      
      const createdMetadata: CanvasMetadata = {
        id: docRef.id,
        name: metadata.name,
        ownerId: metadata.ownerId,
        createdAt: docData.createdAt,
        lastModified: docData.lastModified,
        storagePath: metadata.storagePath,
        fileSize: metadata.fileSize,
        description: metadata.description,
        tags: metadata.tags
      };

      return {
        success: true,
        canvasId: docRef.id,
        metadata: createdMetadata
      };
    } catch (error) {
      console.error('Error creating canvas metadata:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}
